import { AlertTriangle } from 'lucide-react';
import { FC, useEffect, useState } from 'react';

import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { navGuardService } from 'features/common/utils/navGuardService';
import styles from './NavigationGuard.module.css';

/**
 * NavigationGuard
 *
 * Renders an on-brand confirmation dialog when react-router's <Prompt> fires.
 * Mount once in App — it's silent unless a navigation attempt is blocked.
 *
 * Requires BrowserRouter to be configured with:
 *   getUserConfirmation={(msg, cb) => navGuardService.request(msg, cb)}
 */
export const NavigationGuard: FC = () => {
	const [isPending, setIsPending] = useState(false);

	useEffect(() => navGuardService.subscribe(() => setIsPending(navGuardService.isPending())), []);

	// position: fixed inside an auto-height iframe is relative to the iframe document origin,
	// not the browser viewport. Scroll both the iframe and the parent page to top so the
	// dialog appears in the visible area regardless of how far the user has scrolled.
	useEffect(() => {
		if (isPending) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
			window.parent.postMessage({ action: 'scroll-to-top' }, '*');
		}
	}, [isPending]);

	if (!isPending) return null;

	return (
		<div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="navguard-title">
			<div className={styles.dialog}>
				<div className={styles.iconWrap} aria-hidden="true">
					<AlertTriangle className={styles.icon} />
				</div>
				<h2 id="navguard-title" className={styles.title}>
					Unsaved changes
				</h2>
				<p className={styles.message}>You have unsaved changes that will be lost if you navigate away.</p>
				<div className={styles.actions}>
					<PunchupButton type="button" variant="secondary" text="Stay" onClick={() => navGuardService.resolve(false)} />
					<PunchupButton
						type="button"
						variant="destructive"
						text="Leave anyway"
						onClick={() => navGuardService.resolve(true)}
					/>
				</div>
			</div>
		</div>
	);
};
